import { Select } from '@amzn/awsui-components-react-v2';

/**
 * Supported BCP-47 locale codes for a delivery language's optional `locale_code`.
 *
 * This list MUST stay in sync with:
 *   - the BusinessDataServiceLambda `VALID_CATALOG_LOCALE_CODES` constant, and
 *   - CatalogService's `validTCLanguageCodes` set (the ultimate source of truth),
 * since the localized course-description lookup keys on these exact values.
 *
 * NOTE: "fi-FN" is a known quirk in CCS (Finnish should be "fi-FI"); it is
 * mirrored verbatim here on purpose so an entry CCS accepts is not rejected.
 */
export const DELIVERY_LANGUAGE_LOCALE_CODES: ReadonlyArray<{
    code: string;
    label: string;
}> = [
    { code: 'ar-SA', label: 'Arabic (ar-SA)' },
    { code: 'zh-CN', label: 'Chinese, Simplified (zh-CN)' },
    { code: 'zh-TW', label: 'Chinese, Traditional (zh-TW)' },
    { code: 'de-DE', label: 'German (de-DE)' },
    { code: 'el-GR', label: 'Greek (el-GR)' },
    { code: 'en-US', label: 'English (en-US)' },
    { code: 'fr-FR', label: 'French (fr-FR)' },
    { code: 'ja-JP', label: 'Japanese (ja-JP)' },
    { code: 'he-IL', label: 'Hebrew (he-IL)' },
    { code: 'id-ID', label: 'Indonesian (id-ID)' },
    { code: 'it-IT', label: 'Italian (it-IT)' },
    { code: 'ko-KR', label: 'Korean (ko-KR)' },
    { code: 'pl-PL', label: 'Polish (pl-PL)' },
    { code: 'pt-BR', label: 'Portuguese, Brazil (pt-BR)' },
    { code: 'es-419', label: 'Spanish, LATAM (es-419)' },
    { code: 'es-ES', label: 'Spanish, Spain (es-ES)' },
    { code: 'th-TH', label: 'Thai (th-TH)' },
    { code: 'tr-TR', label: 'Turkish (tr-TR)' },
    { code: 'uk-UA', label: 'Ukrainian (uk-UA)' },
    { code: 'vi-VN', label: 'Vietnamese (vi-VN)' },
    { code: 'sv-SE', label: 'Swedish (sv-SE)' },
    { code: 'hi-IN', label: 'Hindi (hi-IN)' },
    { code: 'fi-FN', label: 'Finnish (fi-FN)' },
];

// Sentinel option letting an admin explicitly leave `locale_code` unset. Its
// empty `id` is omitted by the create payload builder, and sent as an explicit
// empty string by the update builder so a previously-set code can be cleared.
export const DELIVERY_LANGUAGE_LOCALE_NONE_OPTION: Select.Option = {
    id: '',
    label: 'None',
};

export const deliveryLanguageLocaleOptions: Array<Select.Option> = [
    DELIVERY_LANGUAGE_LOCALE_NONE_OPTION,
    ...DELIVERY_LANGUAGE_LOCALE_CODES.map(({ code, label }): Select.Option => ({
        id: code,
        label,
    })),
];

export const deliveryLanguageLocaleLookup: {
    [key: string]: Select.Option;
} = deliveryLanguageLocaleOptions.reduce(
    (lookup: { [key: string]: Select.Option }, option) => {
        lookup[option.id as string] = option;
        return lookup;
    },
    {},
);
